import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { withAuthorization, withEmailVerification } from '../Session';
import { withFirebase } from '../Firebase';
import { UserList } from '../Users';
import AdminDashboard from './AdminDashboard';
import ActivityLog from './ActivityLog';
import AdminSettings from './AdminSettings';
import * as ROLES from '../../constants/roles';
import vcontrolAPI from '../../services/documentApi';
import './admin.css';

const AdminPage = ({ firebase }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [projectsByOrg, setProjectsByOrg] = useState({});
  const [totalMessages, setTotalMessages] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [serverHealth, setServerHealth] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [shapeCountsByOrg, setShapeCountsByOrg] = useState({});
  const activityCleanupRef = useRef([]);

  const extractOrganizations = useCallback((usersList) => {
    const orgs = new Set();
    usersList.forEach(user => {
      if (user.roles) {
        Object.keys(user.roles).forEach(org => {
          if (user.roles[org] !== null) orgs.add(org);
        });
      }
    });
    return Array.from(orgs);
  }, []);

  const fetchAllOrgProjects = useCallback(async (orgList) => {
    const results = {};
    await Promise.all(
      orgList.map(async (org) => {
        try {
          const projects = await firebase.doGetOrganizationProjects(org);
          if (projects && projects.length > 0) {
            results[org] = projects;
          }
        } catch { /* skip */ }
      })
    );
    setProjectsByOrg(results);
    return results;
  }, [firebase]);

  // Fetch shape/page counts for org→project pairs (all versions)
  const fetchShapeCounts = useCallback(async (orgProjects) => {
    const counts = {};
    const tasks = [];
    Object.entries(orgProjects).forEach(([org, projects]) => {
      projects.forEach(project => {
        tasks.push(
          firebase.doGetProjectVersions(org, project.id)
            .then(versions => {
              if (!versions || versions.length === 0) return null;
              return firebase.doGetAllPagesWithShapes(org, project.id, versions);
            })
            .then(versionPagesMap => {
              if (!versionPagesMap) return;
              if (!counts[org]) counts[org] = { pages: 0, projects: 0 };
              let totalPages = 0;
              versionPagesMap.forEach((pageSet) => {
                totalPages += pageSet.size;
              });
              if (totalPages > 0) {
                counts[org].pages += totalPages;
                counts[org].projects += 1;
              }
            })
            .catch(() => { })
        );
      });
    });
    await Promise.all(tasks);
    setShapeCountsByOrg(counts);
  }, [firebase]);

  // Set up cross-org activity listeners
  const setupActivityListeners = useCallback((orgList) => {
    // Clean up previous listeners
    activityCleanupRef.current.forEach(fn => fn());
    activityCleanupRef.current = [];

    const allLogs = {};
    const updateCombined = () => {
      const combined = Object.values(allLogs).flat();
      combined.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      setActivityLogs(combined.slice(0, 200));
    };

    orgList.forEach(org => {
      firebase.onOrgActivityFeed(org, (orgLogs) => {
        allLogs[org] = orgLogs.map(log => ({ ...log, organization: org }));
        updateCombined();
      }, 50);
      activityCleanupRef.current.push(() => firebase.offOrgActivityFeed(org, 50));
    });
  }, [firebase]);

  useEffect(() => {
    setLoading(true);

    const onUsersUpdate = snapshot => {
      const usersObject = snapshot.val();
      if (usersObject) {
        const usersList = Object.keys(usersObject).map(key => ({
          ...usersObject[key],
          uid: key,
        }));
        setUsers(usersList);

        const orgList = extractOrganizations(usersList);
        setOrganizations(orgList);

        fetchAllOrgProjects(orgList).then(orgProjects => {
          fetchShapeCounts(orgProjects);
        });

        setupActivityListeners(orgList);

        if (orgList.length > 0) {
          vcontrolAPI.getOrganizationDocuments(orgList)
            .then(docs => setDocuments(Array.isArray(docs) ? docs : []))
            .catch(() => setDocuments([]));
        }
      } else {
        setUsers([]);
        setOrganizations([]);
        setProjectsByOrg({});
        setDocuments([]);
      }
      setLoading(false);
    };

    firebase.users().once('value', onUsersUpdate).catch(() => {
      setUsers([]);
      setOrganizations([]);
      setProjectsByOrg({});
      setDocuments([]);
      setLoading(false);
    });

    firebase.messages().once('value', snapshot => {
      const msgs = snapshot.val();
      setTotalMessages(msgs ? Object.keys(msgs).length : 0);
    });

    vcontrolAPI.checkServerHealth()
      .then(setServerHealth)
      .catch(() => setServerHealth({ healthy: false, status: 'unreachable', responseTime: 0 }));

    return () => {
      activityCleanupRef.current.forEach(fn => fn());
    };
  }, [firebase, extractOrganizations, fetchAllOrgProjects, fetchShapeCounts, setupActivityListeners]);

  const refreshHealth = useCallback(async () => {
    try {
      const result = await vcontrolAPI.checkServerHealth();
      setServerHealth(result);
      return result;
    } catch {
      const fallback = { healthy: false, status: 'unreachable', responseTime: 0 };
      setServerHealth(fallback);
      return fallback;
    }
  }, []);

  const totalProjects = Object.values(projectsByOrg).reduce(
    (sum, projects) => sum + projects.length, 0
  );

  const tabs = [
    { id: 'overview', label: t('admin.tab_overview'), icon: '📊', count: null },
    { id: 'users', label: t('admin.tab_users'), icon: '👥', count: users.length },
    { id: 'activity', label: t('admin.tab_activity'), icon: '📈', count: activityLogs.length },
    { id: 'settings', label: t('admin.tab_settings'), icon: '⚙️', count: null },
  ];

  return (
    <div className='main'>
      <div className="admin-container">
        <header className="admin-header">
          <div className="admin-header-top">
            <div className="admin-title-group">
              <h2><span>🛠️</span> {t('admin.title')}</h2>
              <p className="admin-subtitle">{t('admin.subtitle')}</p>
            </div>
            <div className="admin-header-badges">
              <span className={`health-badge ${serverHealth?.healthy ? 'healthy' : 'unhealthy'}`}>
                <span className="health-dot" />
                {serverHealth?.healthy ? 'API Online' : serverHealth ? 'API Down' : '...'}
              </span>
              {serverHealth?.version && (
                <span className="version-badge">v{serverHealth.version}</span>
              )}
            </div>
          </div>
        </header>

        <nav className="admin-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
              {tab.count !== null && <span className="tab-badge">{tab.count}</span>}
            </button>
          ))}
        </nav>

        <section className="admin-content">
          {activeTab === 'overview' && (
            <AdminDashboard
              users={users}
              loading={loading}
              organizations={organizations}
              projectsByOrg={projectsByOrg}
              totalProjects={totalProjects}
              totalMessages={totalMessages}
              documents={documents}
              serverHealth={serverHealth}
              activityLogs={activityLogs}
              shapeCountsByOrg={shapeCountsByOrg}
              t={t}
            />
          )}

          {activeTab === 'users' && <UserList users={users} loading={loading} activityLogs={activityLogs} />}

          {activeTab === 'activity' && (
            <ActivityLog logs={activityLogs} users={users} />
          )}

          {activeTab === 'settings' && (
            <AdminSettings
              firebase={firebase}
              serverHealth={serverHealth}
              organizations={organizations}
              projectsByOrg={projectsByOrg}
              documents={documents}
              users={users}
              onRefreshHealth={refreshHealth}
            />
          )}
        </section>
      </div>
    </div>
  );
};

const condition = authUser =>
  authUser && authUser.roles && authUser.roles[ROLES.ADMIN] === ROLES.ADMIN;

const HOCsAppliedToHomePage = withAuthorization(condition)(withFirebase(AdminPage));
const FinalHomePageComponent = withEmailVerification(HOCsAppliedToHomePage);

export default FinalHomePageComponent;
